import { get } from 'lodash';
import React, { useContext } from 'react';
import {
    useSearchParams,
    SetURLSearchParams,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { NavGraph, NavGraphItem } from 'src/types/navGraph';
import {
    generateFilter,
    getFilteredNavGraph,
    getItemByPath,
    makeInitialFilter,
} from 'src/utils/hierarchyUtils';
import { FilterItem } from '../HierarchyFilters';
import { useNavGraph } from './NavGraphProvider';
import { usePreferences } from './PreferencesProvider';

export type TabContext = {
    path: string;
    isDynamic: boolean;
};

export interface BreadCrumbFilterContextType {
    breadCrumbFilters: FilterItem[];
    tabContext: Map<string, TabContext>;
    setTabQueryParam: (value: string) => void;
    removeQueryParams: () => void;
    getCurrTab: () => string | null;
}

export const BreadCrumbFilterContext =
    React.createContext<BreadCrumbFilterContextType>({
        breadCrumbFilters: [],
        tabContext: new Map(),
        setTabQueryParam: () => undefined,
        removeQueryParams: () => undefined,
        getCurrTab: () => null,
    });

export const useBreadCrumbFilters = () => useContext(BreadCrumbFilterContext);

interface BreadCrumbFilterProviderProps {
    children: React.ReactNode;
}

export const BreadCrumbFilterProvider = ({
    children,
}: BreadCrumbFilterProviderProps) => {
    let breadCrumbFilters: FilterItem[] = [];
    const PATH_DELIMITER = '/';

    const { navGraph } = useNavGraph();
    const { refinementToggleOn, userPreferences } = usePreferences();
    const navigate = useNavigate();
    const [queryParams, setQueryParams]: [URLSearchParams, SetURLSearchParams] =
        useSearchParams();

    const updateQueryParam = (key: string, value: string) => {
        const newParams = new URLSearchParams(queryParams);
        newParams.set(key, value);
        setQueryParams(newParams, { replace: false });
    };

    const setTabQueryParam = (value: string) => updateQueryParam('tab', value);

    const removeQueryParams = () => {
        setQueryParams({}, { replace: false });
    };

    const convertPathListIntoFilters = (pathList: string[]): FilterItem[] => {
        return makeInitialFilter(filteredGraph)
            .concat(
                pathList
                    .map((fp) => getItemByPath(fp, filteredGraph))
                    .map(generateFilter)
            )
            .reduce((acc: FilterItem[], fi, idx) => {
                if (idx >= 1) {
                    acc[idx - 1].itemSelected = fi.title;
                }

                acc[idx] = fi;
                return acc;
            }, Array(pathList.length).fill(undefined) as FilterItem[]);
    };

    const convertBreadCrumbPathIntoPathList = (
        pathForClickedCrumb: string
    ): string[] => {
        const keysToPathForCrumb: string[] =
            pathForClickedCrumb.split(PATH_DELIMITER);
        let pathList: string[] = [];
        let currNavGraph: NavGraph | undefined = filteredGraph;

        keysToPathForCrumb.forEach((k) => {
            const currNavGraphItem = get(currNavGraph, k);
            if (!currNavGraphItem) {
                // should not be possible
                console.error('Key not found in Nav Graph!');
            } else if (!currNavGraphItem.isDynamic) {
                pathList.push(currNavGraphItem.path);
                currNavGraph = currNavGraphItem.children;
            }
        });

        return pathList;
    };

    const getCurrTab = () => {
        const queryTab = queryParams.get('tab');
        if (
            queryTab === null &&
            window.location.hash.includes('?') &&
            window.location.hash.includes('tab=')
        ) {
            // Just get the tab value and not what's after
            try {
                return window.location.hash.split('tab=')[1].split('&')[0];
            } catch (e) {
                navigate('/error');
            }
        }
        return queryTab;
    };

    let filteredGraph: NavGraph = {};
    if (navGraph && Object.keys(navGraph).length) {
        filteredGraph = JSON.parse(JSON.stringify(navGraph)) as NavGraph;
        getFilteredNavGraph(filteredGraph, userPreferences, refinementToggleOn);
    }

    const tabContext: Map<string, TabContext> = new Map();
    if (
        filteredGraph &&
        Object.keys(filteredGraph).length &&
        tabContext.size === 0
    ) {
        const stack: [string, NavGraphItem][] = Object.entries(filteredGraph);
        while (stack.length) {
            const [itemKey, currItem]: [string, NavGraphItem] = stack.pop()!;
            const path = currItem.path
                .split(PATH_DELIMITER)
                .filter(Boolean)
                .slice(1) // ignore first 357 path key
                .join(PATH_DELIMITER);
            tabContext.set(itemKey, { path, isDynamic: !!currItem.isDynamic });

            if (currItem.children) {
                Object.entries(currItem.children).forEach(([key, c]) =>
                    stack.push([key, c])
                );
            }
        }
    }

    const clickedTab: string | null = getCurrTab();
    if (filteredGraph && Object.keys(filteredGraph).length) {
        if (!clickedTab) {
            breadCrumbFilters = makeInitialFilter(filteredGraph);
        } else if (tabContext.size) {
            const pathForClickedCrumb: string | undefined =
                tabContext.get(clickedTab)?.path;

            if (pathForClickedCrumb) {
                const pathList =
                    convertBreadCrumbPathIntoPathList(pathForClickedCrumb);
                const filtersToApply: FilterItem[] =
                    convertPathListIntoFilters(pathList);

                breadCrumbFilters = filtersToApply;
            }
        }
    }

    const value: BreadCrumbFilterContextType = {
        breadCrumbFilters,
        tabContext,
        setTabQueryParam,
        removeQueryParams,
        getCurrTab,
    };

    return (
        <BreadCrumbFilterContext.Provider value={value}>
            {children}
        </BreadCrumbFilterContext.Provider>
    );
};
